import { GenericDropdownOption } from 'App.types';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import useSelectProps, { SelectProps } from 'Hooks/useSelectProps';
import { ValueType } from 'react-select';

const queryOptions = creatorbaseQueries.clientPayee.options.infinite;

export type PayeeOption = GenericDropdownOption<number> &
    Pick<creatorbase.ClientPayee, 'invitation_sent_at' | 'is_payable' | 'tipalti_payee_id'>;

export const mapClientPayeeToOption = (payee: creatorbase.ClientPayee): PayeeOption => ({
    value: payee.id,
    label: payee.email_address,
    invitation_sent_at: payee.invitation_sent_at,
    is_payable: payee.is_payable,
    tipalti_payee_id: payee.tipalti_payee_id,
});

type Params<TIsMulti extends boolean> = {
    initialValue?: ValueType<PayeeOption, TIsMulti>;
    params?: Pick<creatorbase.GetClientPayeesParams, 'client_id' | 'is_payable'>;
    isMulti?: TIsMulti;
};

type PropGetter<TIsMulti extends boolean> = (
    override?: Pick<SelectProps<PayeeOption, TIsMulti>, 'styles' | 'onChange' | 'noOptionsMessage' | 'onKeyDown'>
) => SelectProps<PayeeOption, TIsMulti>;

type Return<TIsMulti extends boolean> = {
    getProps: PropGetter<TIsMulti>;
    setValue: (value: ValueType<PayeeOption, TIsMulti>) => void;
};

export default function usePayeeSelect<TIsMulti extends boolean = false>({
    initialValue,
    params,
    isMulti = false as TIsMulti,
}: Params<TIsMulti> = {}): Return<TIsMulti> {
    const { debouncedSearchValue, getProps, setValue } = useSelectProps<PayeeOption, TIsMulti>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery(
        queryOptions({ search: debouncedSearchValue, page_size: 25, ...params })
    );
    const options: PayeeOption[] =
        data?.pages.flatMap((page) => {
            return page.results.map(mapClientPayeeToOption);
        }) ?? [];

    return {
        getProps: (override) => ({
            ...getProps({
                options,
                noOptionsMessage: () => 'No payees found',
                isLoading: isFetching,
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
                ...override,
            }),
            isMulti: isMulti as TIsMulti,
        }),
        setValue,
    };
}
